import type { IconProps } from '@phosphor-icons/react'
import { forwardRef } from 'react'
import { cn } from './primitives'

/**
 * Marks for the pages carried over from the old Aliens Dashboard (control room, Aliens Courier telemetry,
 * general ledger, admin): 24-unit grid, one hue each, drawn inline like MaterialsIcons / FinanceIcons so they
 * keep their colour in the navigation. Phosphor-compatible (size / weight / className).
 */
export type DashIconKey =
  | 'commandCenter' | 'emergency' | 'alertRules' | 'confirmations' | 'courierApp'
  | 'journal' | 'chart' | 'ledger' | 'periods' | 'activity' | 'masterData' | 'profile'

export const DASH_ICON_TONE: Record<DashIconKey, { fg: string; bg: string }> = {
  commandCenter: { fg: '#4f46e5', bg: '#ecebfd' },
  emergency: { fg: '#dc2626', bg: '#fdeaea' },
  alertRules: { fg: '#d97706', bg: '#fdf1dc' },
  confirmations: { fg: '#16a34a', bg: '#e7f6ef' },
  courierApp: { fg: '#0891b2', bg: '#e0f4f8' },
  journal: { fg: '#0d9488', bg: '#e0f5f2' },
  chart: { fg: '#2563eb', bg: '#e6eefc' },
  ledger: { fg: '#7c3aed', bg: '#efe8fd' },
  periods: { fg: '#b45309', bg: '#fbefdc' },
  activity: { fg: '#0284c7', bg: '#e2f1fb' },
  masterData: { fg: '#db2777', bg: '#fde8f1' },
  profile: { fg: '#475569', bg: '#eef1f5' },
}

const S = { strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

function paths(key: DashIconKey, fg: string) {
  switch (key) {
    case 'commandCenter':
      // a radar dish: the whole operation on one screen
      return (<>
        <circle cx="12" cy="12" r="8.5" fill={fg} opacity=".14" stroke={fg} {...S} />
        <circle cx="12" cy="12" r="4.8" stroke={fg} strokeWidth="1.3" opacity=".7" />
        <path d="M12 12 18 6.4" stroke={fg} {...S} />
        <circle cx="12" cy="12" r="1.6" fill={fg} />
        <circle cx="15.6" cy="15" r="1.3" fill={fg} />
        <circle cx="8" cy="9.2" r="1" fill={fg} opacity=".8" />
      </>)
    case 'emergency':
      // a siren with light rays
      return (<>
        <path d="M6.5 18v-5.5a5.5 5.5 0 0 1 11 0V18" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M4.5 18h15v2.5h-15z" fill={fg} stroke={fg} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M12 3v1.8M4.2 6.2l1.3 1.3M19.8 6.2l-1.3 1.3" stroke={fg} {...S} />
        <path d="M10 12.5a2 2 0 0 1 2-2" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
      </>)
    case 'alertRules':
      // a bell with slider knobs: thresholds
      return (<>
        <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.5h-15L6 16.5Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M10 20.5a2 2 0 0 0 4 0" stroke={fg} {...S} />
        <path d="M9 10.5h6M9 13.5h6" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="10.5" cy="10.5" r="1.2" fill={fg} />
        <circle cx="13.5" cy="13.5" r="1.2" fill={fg} />
      </>)
    case 'confirmations':
      // a chat bubble with a check
      return (<>
        <path d="M4 11.5a7.5 7 0 1 1 3.4 5.9L4 18.5l1.1-3.3A6.8 6.8 0 0 1 4 11.5Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="m8.4 11.6 2.4 2.4 4.8-4.8" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>)
    case 'courierApp':
      // a phone with a courier pin
      return (<>
        <rect x="6.5" y="2.8" width="11" height="18.4" rx="2.2" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M10.5 5.3h3" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M12 16.8s-3.2-3-3.2-5.2a3.2 3.2 0 0 1 6.4 0c0 2.2-3.2 5.2-3.2 5.2Z" fill={fg} stroke={fg} strokeWidth="1.2" strokeLinejoin="round" />
        <circle cx="12" cy="11.6" r="1.1" fill="#fff" />
      </>)
    case 'journal':
      // an open book of entries
      return (<>
        <path d="M3.5 5.5c2.8-1 5.6-1 8.5.8v13c-2.9-1.8-5.7-1.8-8.5-.8v-13Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M20.5 5.5c-2.8-1-5.6-1-8.5.8v13c2.9-1.8 5.7-1.8 8.5-.8v-13Z" fill={fg} opacity=".32" stroke={fg} {...S} />
        <path d="M6 9.5h3.5M6 12.5h3.5M14.5 9.5H18M14.5 12.5H18" stroke={fg} strokeWidth="1.2" strokeLinecap="round" />
      </>)
    case 'chart':
      // a tree of accounts
      return (<>
        <rect x="9" y="2.8" width="6" height="4.6" rx="1.2" fill={fg} stroke={fg} strokeWidth="1.2" />
        <path d="M12 7.4v3.3M6 13.5v-2.8h12v2.8" stroke={fg} {...S} />
        <rect x="3" y="13.5" width="6" height="4.6" rx="1.2" fill={fg} opacity=".2" stroke={fg} {...S} />
        <rect x="15" y="13.5" width="6" height="4.6" rx="1.2" fill={fg} opacity=".2" stroke={fg} {...S} />
        <path d="M5 16h2M17 16h2" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
      </>)
    case 'ledger':
      // a scale: debit and credit in balance
      return (<>
        <path d="M12 4v15.5M7.5 20h9" stroke={fg} {...S} />
        <path d="M4.5 7.5h15" stroke={fg} {...S} />
        <path d="M4.5 7.5 2.5 13h4l-2-5.5ZM19.5 7.5l-2 5.5h4l-2-5.5Z" fill={fg} opacity=".25" stroke={fg} strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="12" cy="4.5" r="1.5" fill={fg} />
      </>)
    case 'periods':
      // a calendar with a lock
      return (<>
        <rect x="3.5" y="5" width="17" height="15" rx="2" fill={fg} opacity=".16" stroke={fg} {...S} />
        <path d="M3.5 9.5h17M8 3v4M16 3v4" stroke={fg} {...S} />
        <rect x="9.3" y="13.2" width="5.4" height="4.3" rx="1" fill={fg} />
        <path d="M10.4 13.2v-1.1a1.6 1.6 0 0 1 3.2 0v1.1" stroke={fg} strokeWidth="1.3" />
      </>)
    case 'activity':
      // a pulse line inside a window
      return (<>
        <rect x="3" y="4" width="18" height="16" rx="2.4" fill={fg} opacity=".14" stroke={fg} {...S} />
        <path d="M5.5 13h3l1.8-4.2 2.8 7.4 2-4.2h3.4" stroke={fg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </>)
    case 'masterData':
      // a database with a magnifier
      return (<>
        <ellipse cx="10" cy="6" rx="6" ry="2.5" fill={fg} opacity=".25" stroke={fg} {...S} />
        <path d="M4 6v10c0 1.4 2.7 2.5 6 2.5M16 6v4" stroke={fg} {...S} />
        <path d="M4 11c0 1.4 2.7 2.5 6 2.5" stroke={fg} strokeWidth="1.3" opacity=".7" />
        <circle cx="16.5" cy="15.5" r="3.3" fill="#fff" stroke={fg} {...S} />
        <path d="m19 18 2 2" stroke={fg} strokeWidth="2" strokeLinecap="round" />
      </>)
    case 'profile':
      // a person inside a card
      return (<>
        <rect x="3" y="4" width="18" height="16" rx="2.4" fill={fg} opacity=".14" stroke={fg} {...S} />
        <circle cx="9" cy="10.5" r="2.4" fill={fg} />
        <path d="M5.5 16.5a3.6 3.6 0 0 1 7 0" stroke={fg} {...S} />
        <path d="M14.5 9.5h4M14.5 12.5h4M14.5 15.5h2.5" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
      </>)
  }
}

function make(key: DashIconKey) {
  const c = DASH_ICON_TONE[key]
  const Mark = forwardRef<SVGSVGElement, IconProps>(function DashMark({ size = 16, className, weight: _w, mirrored: _m, alt, color: _c, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} {...rest}>
        {paths(key, c.fg)}
      </svg>
    )
  })
  Mark.displayName = `DashIcon.${key}`
  return Mark
}

/** Phosphor-compatible components: `<DASH_ICONS.emergency size={20} />`. */
export const DASH_ICONS = Object.fromEntries((Object.keys(DASH_ICON_TONE) as DashIconKey[]).map((k) => [k, make(k)])) as Record<DashIconKey, ReturnType<typeof make>>

/** Mark on its tinted square (KPI tiles, empty states). */
export function DashIconTile({ name, size = 44 }: { name: DashIconKey; size?: number }) {
  const c = DASH_ICON_TONE[name]
  const I = DASH_ICONS[name]
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-2xl" style={{ width: size, height: size, background: c.bg }}>
      <I size={Math.round(size * 0.58)} />
    </span>
  )
}
